import { isoZ, z } from 'my-zod'
import { RentalProductSchemas } from '../rental-product'
import { dateFns, Iso } from 'iso-fns2'

export const allowedTravellerAgeBandOptions = ['Infant', 'Child', 'Youth', 'Adult', 'Senior'] as const

const viatorRentalProductOption = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Required'),
  assetFamilyId: z.string().min(1, 'Required'),
  capacity: z.number().min(1),
  durationHours: z.number().int().min(1),
  startTimeOptions: isoZ.time().array().min(1),
  allowedTravellerAgeBands: z.enum(allowedTravellerAgeBandOptions).array()
})

const viatorRentalProductAvailability = z.object({
  id: z.string().uuid(),
  startDate: isoZ.date(),
  endDate: isoZ.date(),
  dayOfWeek: z.enum(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).array().min(1, 'Please make a selection'),
  productOptions: z.object({ productOptionId: z.string(), startTime: isoZ.time() }).array()
})

export const viatorRentalProductModel = RentalProductSchemas.rentalProductModel.omit({ platform: true }).augment({
  platform: z.literal('Viator'),
  waiverContentId: z.string(),
  countryCode: z
    .string()
    .min(1, 'Required')
    .regex(/[a-zA-Z]{2}/, 'Invalid Country Code'),
  destinationCode: z.string().min(1, 'Required'),
  destinationName: z.string().min(1, 'Required').max(240),
  description: z.string().min(1, 'Required').max(400),
  minBookableHoursInAdvance: z.number().nullable(),
  productOptions: viatorRentalProductOption.array(),
  availability: viatorRentalProductAvailability.array()
})

export const viatorRentalProductUpdate = viatorRentalProductModel.omit({
  versionId: true,
  platform: true,
  shortId: true,
  lifecycleStatus: true,
  taxPolicyIds: true,
  createdDate: true,
  lastModifiedDate: true
})

export const viatorRentalProductHeaderUpdate = viatorRentalProductUpdate.omit({
  productOptions: true,
  availability: true
})

export const viatorRentalProductOptionUpdate = viatorRentalProductOption

export const viatorRentalProductOptionAvailabilityUpdate = viatorRentalProductAvailability.superRefine((avail, ctx) => {
  if (dateFns.isAfter(avail.endDate, dateFns.add(dateFns.now(), { years: 3 }))) {
    return ctx.addIssue({
      code: 'invalid_date',
      message: 'Repeat End date cannot be more than 3 years from the current date',
      path: ['endDate']
    })
  }

  if (dateFns.isAfter(avail.startDate, avail.endDate)) {
    ctx.addIssue({
      path: ['endDate'],
      message: 'End date cannot be before the start date',
      code: 'custom'
    })
  }
})

export function GetDatesForAvailability(
  event: Pick<z.infer<typeof viatorRentalProductAvailability>, 'startDate' | 'endDate' | 'dayOfWeek'>,
  options: { start?: Iso.Date; end?: Iso.Date } = {}
): Iso.Date[] {
  const startDate = dateFns.isAfter(options.start ?? event.startDate, event.startDate)
    ? options.start ?? event.startDate
    : event.startDate
  const endDate = dateFns.isBefore(options.end ?? event.endDate, event.endDate)
    ? options.end ?? event.startDate
    : event.endDate

  const dateArray: Iso.Date[] = []
  const dayOfWeekNumbers = event.dayOfWeek.map((d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(d))

  const start = dateFns.chain(startDate).toZonedDateTime({ timeZone: 'UTC' }).toInstant().toJsDate().value()
  const end = dateFns.chain(endDate).toZonedDateTime({ timeZone: 'UTC' }).toInstant().toJsDate().value()

  let current = start

  while (current.getTime() <= end.getTime()) {
    if (dayOfWeekNumbers.includes(current.getUTCDay())) {
      dateArray.push(current.toISOString().slice(0, 10) as Iso.Date)
    }
    current.setUTCDate(current.getUTCDate() + 1)
  }
  return dateArray
}
